import type { Contact } from '../models/Contact';
import type { State } from '../models/State';
import type { ContactRole } from '../models/ContactRole';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RHFSelect } from './RHF/RHFSelect';
import { numberOrNull } from '../helpers';
import RHFInput from './RHF/RHFInput';
import { RHFControlledInput } from './RHF/RHFControlledInput';
import { RHFSubmitButton } from './RHF/RHFSubmitButton';
import ContactApi from '../apis/contact_api';
import { contactKeys } from '../queries/ContactQueries';
import { handleError } from '../apis';
import { toast } from 'react-toastify';
import { formatPhoneNumbers } from '../helpers/formatters';

const validator_contact = z.object({
  active: z.boolean(),
  contactId: z.number(),
  applicationUserId: z.string(),
  contactRoleId: z.number('Required!').gt(0, 'Required'),
  name: z.string('Required!').trim().min(1, 'Required'),
  address1: z.string().optional().nullable().nullable(),
  address2: z.string().optional().nullable().nullable(),
  city: z.string().optional().nullable().nullable(),
  stateId: z.number().nullable().nullable(),
  zipcode: z.string().trim().optional().nullable(),
  phoneNumber: z.string().trim().optional().nullable(),
  email: z.string().trim().optional().nullable(),
});

type ContactFormValues = z.infer<typeof validator_contact>;

export default function AddUpdateContactForm({
  contact,
  states,
  contactRoles,
}: {
  contact: Contact;
  states: State[];
  contactRoles: ContactRole[];
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    defaultValues: contact,
    resolver: zodResolver(validator_contact),
  });

  const activeValue = useWatch({ control, name: 'active' });

  const onHandleSubmit = async (values: ContactFormValues) => {
    const toAdd = {
      contactId: values.contactId ? values.contactId : 0,
      active: values.active,
      applicationUserId: values.applicationUserId
        ? values.applicationUserId
        : '',
      contactRoleId: values.contactRoleId > 0 ? values.contactRoleId : 5,
      name: values.name ? values.name : '',
      address1: values.address1 ? values.address1 : null,
      address2: values.address2 ? values.address2 : null,
      city: values.city ? values.city : null,
      stateId: values.stateId ? values.stateId : null,
      zipcode: values.zipcode ? values.zipcode : null,
      phoneNumber: values.phoneNumber ? values.phoneNumber : null,
      email: values.email ? values.email : null,
    };

    let res: Contact;
    try {
      if (contact.contactId > 0) {
        res = await ContactApi.updateContact(toAdd as Contact);
      } else {
        res = await ContactApi.createContact(toAdd as Contact);
      }
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
      return navigate(`/contacts/${res.contactId}`);
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(message);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onHandleSubmit)}
      className='flex flex-col items-center justify-center'
    >
      <div className={`mb-2 flex py-2`}>
        <div
          className={`min-w-[275px] flex justify-center  rounded-lg bg-gray-100 px-4 py-3 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-300`}
        >
          <label className='flex items-center gap-2'>
            <input
              type='checkbox'
              checked={activeValue}
              {...register('active')}
              id='active'
              className={`rounded border-gray-300 bg-gray-100 text-teal-600 focus:ring-2 focus:ring-teal-500 dark:border-gray-600 ${
                activeValue ? 'bg-teal-600 dark:bg-teal-600' : ''
              } dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-teal-600`}
            />
            Active
          </label>
        </div>
      </div>
      <div className='alpaca-form-grid'>
        <RHFSelect
          name='contactRoleId'
          label='Role'
          options={contactRoles}
          optionMethod={(contactRoles: ContactRole[]) =>
            contactRoles.map((g) => (
              <option value={g.contactRoleId} key={g.contactRoleId}>
                {g.contactRoleName}
              </option>
            ))
          }
          className='min-w-[275px]'
          register={register('contactRoleId', {
            setValueAs: numberOrNull,
          })}
          errors={errors}
        />
        <RHFInput
          name='name'
          label='Name'
          type='text'
          className='min-w-[275px]'
          register={register('name')}
          errors={errors}
        />
        <RHFInput
          name='address1'
          label='Address 1'
          type='text'
          className='min-w-[275px]'
          register={register('address1')}
          errors={errors}
        />
        <RHFInput
          name='address2'
          label='Address 2'
          type='text'
          className='min-w-[275px]'
          register={register('address2')}
          errors={errors}
        />
        <RHFInput
          name='city'
          label='City'
          type='text'
          className='min-w-[275px]'
          register={register('city')}
          errors={errors}
        />
        <RHFSelect
          name='stateId'
          label='State'
          options={states}
          optionMethod={(states: State[]) =>
            states.map((g) => (
              <option value={g.stateId} key={g.stateId}>
                {g.abbreviation}
              </option>
            ))
          }
          className='min-w-[275px]'
          register={register('stateId', {
            setValueAs: numberOrNull,
          })}
          errors={errors}
        />
        <RHFInput
          name='zipcode'
          label='ZIP Code'
          type='text'
          className='min-w-[275px]'
          register={register('zipcode')}
          errors={errors}
        />
        <RHFControlledInput
          name='phoneNumber'
          label='Phone Number'
          type='text'
          className='min-w-[275px]'
          control={control}
          errors={errors}
          formatter={formatPhoneNumbers}
        />

        <RHFInput
          name='email'
          label='Email Address'
          type='email'
          className='min-w-[275px]'
          register={register('email')}
          errors={errors}
        />
        <div />
        <button
          type='button'
          onClick={() => {
            reset();
            navigate(-1);
          }}
          className='my-2 max-w-[275px] rounded-md border border-slate-950 bg-transparent p-2 text-xs font-bold uppercase hover:border-gray-400 hover:text-gray-400 dark:border-gray-500 dark:hover:border-gray-300 dark:hover:text-gray-300'
        >
          Cancel
        </button>
        <RHFSubmitButton label='Submit' isSubmitting={isSubmitting} />
      </div>
    </form>
  );
}
